import { useLayoutEffect } from 'react';
import { Tour } from 'antd';
import useConfig from 'hooks/useConfig';
import { ExtendedTourStepProps } from 'types/components';
import {
    useGetApiUserPreferencesParsedQuery,
    usePutApiUserPreferencesMutation,
} from 'store/userOrg/enhancedUserOrgApi';
import BuilderImage from 'assets/images/BuilderImage.png';

const getTargetWithScroll = (id: string) => {
    return () => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return element;
        }
        return document.body;
    };
};

const steps: ExtendedTourStepProps[] = [
    {
        title: 'Welcome!',
        description: `Welcome to the Workflow Builder. Let's quickly guide you through the main features.`,
        target: null,
        placement: 'center',
    },
    {
        title: 'Main Navigation',
        description: 'Use the Main Navigation to move between different areas of the application.',
        placement: 'bottomRight',
        target: getTargetWithScroll('menu'),
    },
    {
        title: 'Workflows',
        description: 'Here you can create new workflows and monitor the execution status of existing ones.',
        placement: 'bottomRight',
        target: getTargetWithScroll('workflows'),
    },
    {
        title: 'Project Filter',
        description: 'Apply project-specific filter here to customize the data displayed across the builder.',
        placement: 'bottom',
        target: getTargetWithScroll('globalFilter'),
    },
    {
        title: 'Start Building Workflow',
        description: 'Click here to start building a brand new workflow.',
        placement: 'bottom',
        target: getTargetWithScroll('createNewWorkflow'),
    },
    {
        title: 'Workflow Design Tools',
        description: (
            <div>
                <div>
                    <img
                        src={BuilderImage}
                        alt='Save, Download, Add Buttons'
                        style={{ width: '100%', maxHeight: '100%' }}
                    />
                    <p>Use these buttons for adding components, saving, and downloading your workflow.</p>
                </div>
            </div>
        ),
        target: null,
        placement: 'center',
    },
    {
        title: 'Existing Workflows',
        description: 'View and manage all your existing workflows in this list.',
        placement: 'bottom',
        target: getTargetWithScroll('ExistingWorkflows'),
    },
    {
        title: 'User Settings & Help',
        description: 'Access your user profile, view organization details, and restart this guide from here.',
        placement: 'bottom',
        target: getTargetWithScroll('userSettings'),
    },
];

const BeginTour = () => {
    const { firstBuilderStart, showBuilderGuide, onChangeshowBuilderGuide } = useConfig();

    const [putPreferences] = usePutApiUserPreferencesMutation();
    const { data: preferences } = useGetApiUserPreferencesParsedQuery();

    useLayoutEffect(() => {
        if (!firstBuilderStart) {
            onChangeshowBuilderGuide(true);
        } else {
            onChangeshowBuilderGuide(false);
        }
    }, [firstBuilderStart]);

    const changeshowBuilderGuide = () =>
        !preferences?.firstBuilderStart &&
        putPreferences({
            systemTextJsonJsonElement: {
                ...preferences,
                firstBuilderStart: true,
            },
        });
    return (
        <Tour
            open={showBuilderGuide}
            onClose={() => {
                onChangeshowBuilderGuide(false);
                changeshowBuilderGuide();
            }}
            steps={steps}
            onFinish={() => changeshowBuilderGuide()}
            scrollIntoViewOptions={{ block: 'center' }}
            zIndex={2000}
        />
    );
};

export default BeginTour;
